/** @jsxImportSource @emotion/react */
'use memo'

import { LoadingOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'
import Text from 'src/components/ts-components/Text'

type Size = 'small' | 'default'

type HeaderActionButtonProps = {
  text: string
  onClick: () => void
  disabled?: boolean
  isLoading?: boolean
  variant: 'primary' | 'secondary'
  icon?: React.ReactNode
  textWeight?: 'regular' | 'medium' | 'bold' | 'semiBold'
  size?: Size
}

const sizeMap: Record<
  Size,
  { height: number; padding: string; minWidth: number }
> = {
  default: { height: 40, padding: '8px 16px', minWidth: 120 },
  small: { height: 32, padding: '4px 12px', minWidth: 0 },
}

const HeaderActionButton = ({
  text,
  onClick,
  disabled = false,
  isLoading = false,
  variant,
  icon,
  textWeight = 'regular',
  size = 'default',
}: HeaderActionButtonProps) => {
  const isPrimary = variant === 'primary'
  const isDisabled = disabled || isLoading
  const isSmall = size === 'small'
  const dims = sizeMap[size]
  const contentColor = isDisabled
    ? colors.NEUTRAL_50
    : isPrimary
    ? colors.SECONDARY_300
    : colors.NEUTRAL_0

  return (
    <button
      type='button'
      onClick={onClick}
      disabled={isDisabled}
      css={css`
        display: inline-flex;
        min-width: ${dims.minWidth}px;
        height: ${dims.height}px;
        padding: ${dims.padding};
        justify-content: center;
        align-items: center;
        gap: ${isSmall ? 6 : 8}px;
        border-radius: 8px;
        border: 1px solid
          ${isDisabled
            ? colors.SECONDARY_200
            : isPrimary
            ? colors.PRIMARY
            : colors.NEUTRAL_50};
        background: ${isDisabled
          ? colors.SECONDARY_200
          : isPrimary
          ? colors.PRIMARY
          : 'transparent'};
        cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
        transition: background-color 0.2s ease, border-color 0.2s ease,
          opacity 0.2s ease;

        &:hover:not(:disabled) {
          background: ${isPrimary
            ? colors.PRIMARY_LIGHT
            : colors.SECONDARY_400};
          border-color: ${isPrimary ? colors.PRIMARY_LIGHT : colors.NEUTRAL_0};
        }
      `}
    >
      {isLoading ? (
        <LoadingOutlined
          css={css`
            color: ${contentColor};
            font-size: 16px;
          `}
        />
      ) : (
        <>
          {icon ? <div>{icon}</div> : null}
          <Text
            variant={isSmall ? 'paragraphXSmall' : 'paragraphMedium'}
            weight={textWeight}
            color={contentColor}
            style='margin: 0; white-space: nowrap;'
          >
            {text}
          </Text>
        </>
      )}
    </button>
  )
}

export default HeaderActionButton
